import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, Mail, Wrench, Sofa, Droplets, Sparkles, ThermometerSun, CheckCircle, Filter, Shield, AlertTriangle, Thermometer, Zap, Volume2, AlertCircle, HelpCircle, Award, Headphones, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import ContactCTA from '../components/ContactCTA';

const PiecesHubPage = () => {

  // Hub categories with immersive design
  const hubCategories = [
    {
      id: 'pieces-detachees',
      title: 'Pièces détachées',
      description: 'Pompes de circulation, pompes de massage, blower, cartes électroniques, réchauffeurs…',
      image: 'https://customer-assets.emergentagent.com/job_c10e9852-e9cd-437a-b161-861b66fc2016/artifacts/o6vfk3d7_Plan%20de%20travail%201.png',
      link: '/pieces-detachees-spa',
      icon: Wrench,
      color: '#1E3A5F',
      items: ['Pompes circulation', 'Pompes massage', 'Blower', 'Cartes électroniques'],
      proof: '✓ Large compatibilité avec les principales marques de spa',
      imageStyle: 'object-contain brightness-125',
      cta: 'Voir les pièces'
    },
    {
      id: 'accessoires',
      title: 'Accessoires',
      description: 'Lève-couverture, escalier, appuie-tête, couverture thermique, éclairage…',
      image: 'https://customer-assets.emergentagent.com/job_2ae51f7b-aa1f-4c25-beba-96cbb60a0917/artifacts/m27empjx_leve%20couverture.jpeg',
      link: '/accessoires-spa',
      icon: Sofa,
      color: '#F97316',
      items: ['Lève-couverture', 'Escalier', 'Appuie-tête', 'Couverture'],
      proof: '✓ Paiement sécurisé',
      cta: 'Voir les accessoires'
    },
    {
      id: 'entretien',
      title: 'Entretien',
      description: 'Produits de traitement, filtres, kits d\'analyse, nettoyants, anti-calcaire…',
      image: 'https://customer-assets.emergentagent.com/job_2ae51f7b-aa1f-4c25-beba-96cbb60a0917/artifacts/wc4961a2_hth-spa-coffret-de-traitement-au-chlore-xnes.webp',
      link: '/entretien-spa',
      icon: Droplets,
      color: '#10B981',
      items: ['Produits traitement', 'Filtres', 'Analyse eau', 'Nettoyants'],
      proof: '✓ Solutions efficaces pour l\'entretien du spa',
      cta: 'Voir l\'entretien'
    },
    {
      id: 'filtres-spa',
      title: 'Filtres spa',
      description: 'Cartouches et filtres compatibles – bien choisir selon les dimensions.',
      image: 'https://customer-assets.emergentagent.com/job_b3797b70-2366-4f7a-a978-30c05fd0fea5/artifacts/bgwrs8xp_filtre-spa.jpg',
      link: '/filtres-spa',
      icon: Filter,
      color: '#0EA5E9',
      items: ['Cartouches filtrantes', 'Dimensions standards', 'Toutes marques'],
      proof: '✓ Un doute ? Contactez-nous avant commande',
      cta: 'Voir les filtres',
      imageStyle: 'object-contain'
    },
    {
      id: 'pompes-chaleur',
      title: 'Pompes à chaleur',
      description: 'Réduisez la consommation électrique de votre spa avec une pompe à chaleur adaptée.',
      image: 'https://customer-assets.emergentagent.com/job_2ae51f7b-aa1f-4c25-beba-96cbb60a0917/artifacts/ustcg4bw_Poolex_O-Spa_05.jpg',
      link: '/pompes-a-chaleur-spa',
      icon: ThermometerSun,
      color: '#059669',
      items: ['POOLEX 3kW', 'POOLEX 5kW', 'Étude personnalisée'],
      proof: '✓ Étude gratuite',
      isNew: true,
      cta: 'Découvrir'
    },
    {
      id: 'couvertures-sur-mesure',
      title: 'Couvertures sur mesure',
      description: 'Demande de devis couverture sur mesure – dimensions, radius, options.',
      image: 'https://customer-assets.emergentagent.com/job_b3797b70-2366-4f7a-a978-30c05fd0fea5/artifacts/ifb8vag2_MAT013002B00.jpg',
      link: '/couverture-spa-sur-mesure',
      icon: Shield,
      color: '#8B5CF6',
      items: ['Sur mesure', 'Mousse isolante', 'Coloris au choix'],
      proof: '✓ Devis personnalisé gratuit',
      cta: 'Demander un devis',
      imageStyle: 'object-contain'
    }
  ];

  // Breadcrumbs for SEO
  const breadcrumbs = [
    { name: 'Accueil', url: '/' },
    { name: 'Pièces et accessoires', url: '/pieces-accessoires' }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="pieces-hub-page">
      <SEO
        title="Pièces détachées spa, accessoires et entretien | Livraison France | Atlantique Spa Boutique"
        description="Pièces détachées spa : pompe spa, réchauffeur spa, clavier Balboa, carte électronique, filtre spa. Accessoires et entretien. Livraison France entière 48-72h. Spécialiste spa depuis 2018."
        canonical="/pieces-accessoires"
        breadcrumbs={breadcrumbs}
      />

      {/* JSON-LD Schema for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Pièces détachées spa, accessoires et entretien",
        "description": "Catalogue complet de pièces détachées pour spa : pompes, réchauffeurs, claviers, cartes électroniques, filtres. Accessoires et produits d'entretien. Livraison France entière.",
        "url": "https://atlantiquespaboutique.com/pieces-accessoires",
        "mainEntity": {
          "@type": "ItemList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Pièces détachées spa", "url": "https://atlantiquespaboutique.com/pieces-detachees-spa" },
            { "@type": "ListItem", "position": 2, "name": "Accessoires spa", "url": "https://atlantiquespaboutique.com/accessoires-spa" },
            { "@type": "ListItem", "position": 3, "name": "Entretien spa", "url": "https://atlantiquespaboutique.com/entretien-spa" },
            { "@type": "ListItem", "position": 4, "name": "Filtres spa", "url": "https://atlantiquespaboutique.com/filtres-spa" },
            { "@type": "ListItem", "position": 5, "name": "Pompes à chaleur spa", "url": "https://atlantiquespaboutique.com/pompes-a-chaleur-spa" }
          ]
        },
        "provider": {
          "@type": "Organization",
          "name": "Atlantique Spa Boutique",
          "url": "https://atlantiquespaboutique.com",
          "description": "Spécialiste pièces détachées et accessoires spa en France"
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://atlantiquespaboutique.com/" },
            { "@type": "ListItem", "position": 2, "name": "Pièces et accessoires", "item": "https://atlantiquespaboutique.com/pieces-accessoires" }
          ]
        }
      }) }} />

      {/* ============================================= */}
      {/* HERO SECTION - Avec photo terrain */}
      {/* ============================================= */}
      <section className="relative h-80 lg:h-96 overflow-hidden">
        <img 
          src="/images/terrain/spa-nouveau-1.jpg" 
          alt="Spa installé sur terrasse avec pergola"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/95 via-[#1E3A5F]/85 to-[#1E3A5F]/50" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 text-center w-full">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center gap-2 text-sm text-white/60 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
              <span>/</span>
              <span className="text-white">Pièces et accessoires</span>
            </nav>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Pièces, accessoires et entretien pour spa
            </h1>
            
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-3 leading-relaxed">
              Livraison France métropolitaine (48–72h selon stock)
            </p>
            <p className="text-base text-white/60 max-w-2xl mx-auto mb-8">
              Installation possible localement : 44 / 85 / 56 / 49 (sur devis)
            </p>

            {/* Quick stats */}
            <div className="flex flex-wrap justify-center gap-6 text-white/80">
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-[#F97316]" />
                <span>Livraison 48-72h</span>
              </div>
              <div className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-[#10B981]" />
                <span>Installation sur devis</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#60A5FA]" />
                <span>Conseil expert</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================= */}
      {/* SEO INTRODUCTION - Contenu optimisé */}
      {/* ============================================= */}
      <section className="py-12 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Votre spécialiste pièces détachées spa en France
            </h2>
            <p className="text-[#475569] text-lg leading-relaxed mb-4">
              Trouvez la <strong>pièce détachée spa</strong> qu'il vous faut parmi notre sélection : <strong>pompe spa</strong> (circulation, massage), 
              <strong> réchauffeur spa</strong>, <strong>clavier spa</strong> Balboa et Gecko, <strong>carte électronique spa</strong>, 
              <strong> filtre spa</strong> et bien plus.
            </p>
            <p className="text-[#475569] text-base leading-relaxed mb-4">
              Nous proposons des pièces détachées compatibles avec la majorité des marques de spas présentes sur le marché : 
              <strong> Balboa, Gecko, Waterway, LX, Wellis, Canadian Spa, Hydropool, Jacuzzi, Sundance, Master Spas</strong> et <strong>Dimension One</strong>.
            </p>
            <p className="text-[#475569] text-base leading-relaxed">
              Si vous avez un doute sur la compatibilité d'une pièce, vous pouvez utiliser notre{' '}
              <Link to="/contact" className="text-[#F97316] hover:underline font-medium">formulaire de diagnostic</Link>{' '}
              afin d'identifier la référence adaptée à votre spa.
            </p>
          </div>
          
          {/* Keywords cloud for SEO */}
          <div className="flex flex-wrap justify-center gap-2">
            {['Pompe spa', 'Réchauffeur spa', 'Clavier Balboa', 'Carte électronique', 'Filtre spa', 'Blower spa', 'Pièces Gecko', 'Pompe LX', 'Pièces Waterway'].map((keyword) => (
              <span key={keyword} className="bg-[#F8FAFC] text-[#475569] text-sm px-4 py-2 rounded-full border border-[#E2E8F0]">
                {keyword}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================= */}
      {/* MAIN CATEGORIES - 6 cartes visuelles */}
      {/* ============================================= */}
      <section className="py-20 lg:py-28 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {hubCategories.map((category) => {
              const IconComponent = category.icon;
              return (
                <Link
                  key={category.id}
                  to={category.link}
                  className="group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2"
                  data-testid={`hub-card-${category.id}`}
                >
                  {/* Background Image */}
                  <div className="absolute inset-0 bg-[#1a2e3d]">
                    <img
                      src={category.image}
                      alt={category.title}
                      className={`w-full h-full transition-transform duration-700 group-hover:scale-110 ${category.imageStyle || 'object-cover'}`}
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20 group-hover:from-black/95 transition-all duration-500" />
                  </div>

                  {/* Content */}
                  <div className="relative z-10 p-8 min-h-[420px] flex flex-col">
                    {/* Badge Nouveau */}
                    {category.isNew && (
                      <div className="absolute top-6 right-6">
                        <span className="inline-flex items-center gap-1.5 bg-emerald-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                          <Sparkles className="w-3 h-3" />
                          NOUVEAU
                        </span>
                      </div>
                    )}
                    
                    {/* Spacer to push content down */}
                    <div className="flex-grow min-h-[80px]" />

                    {/* Icon badge */}
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110"
                      style={{ backgroundColor: category.color }}
                    >
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 group-hover:text-white transition-colors min-h-[72px]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {category.title}
                    </h2>

                    {/* Description */}
                    <p className="text-white/80 text-base leading-relaxed mb-6 min-h-[72px]">
                      {category.description}
                    </p>

                    {/* Item tags */}
                    <div className="flex flex-wrap gap-2 mb-6 min-h-[68px]">
                      {category.items.map((item) => (
                        <span
                          key={item}
                          className="bg-white/10 backdrop-blur-sm text-white/90 text-xs px-3 py-1.5 rounded-full h-fit"
                        >
                          {item}
                        </span>
                      ))}
                    </div>

                    {/* Micro-proof */}
                    <div className="text-[#10B981] text-sm font-medium mb-4">
                      {category.proof}
                    </div>

                    {/* CTA Button */}
                    <div className="flex items-center gap-2 text-white font-semibold group-hover:gap-4 transition-all duration-300">
                      <span 
                        className="px-6 py-3 rounded-xl text-white transition-all duration-300"
                        style={{ backgroundColor: category.color }}
                      >
                        {category.cta || 'Découvrir'}
                      </span>
                      <ArrowRight className="w-5 h-5 transform group-hover:translate-x-2 transition-transform duration-300" style={{ color: category.color }} />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================= */}
      {/* DIAGNOSTIC PANNE SPA - Section SEO */}
      {/* ============================================= */}
      <section className="py-16 bg-[#FEF3C7]/30 border-b border-[#F59E0B]/20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#F59E0B]/10 text-[#B45309] px-4 py-2 rounded-full text-sm font-medium mb-4">
              <AlertTriangle className="w-4 h-4" />
              Diagnostic de panne spa
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Votre spa a un problème ? Identifiez la panne
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Les pannes de spa les plus fréquentes et les pièces de rechange correspondantes. 
              Cliquez sur votre problème pour trouver la solution.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Panne 1: Spa ne chauffe plus */}
            <Link 
              to="/pieces-detachees-spa?category=rechauffeurs"
              className="bg-white rounded-xl p-6 border border-[#E2E8F0] hover:border-[#F97316] hover:shadow-lg transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-100 rounded-lg group-hover:bg-red-200 transition-colors">
                  <Thermometer className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A] mb-2 group-hover:text-[#F97316] transition-colors">
                    Mon spa ne chauffe plus
                  </h3>
                  <p className="text-sm text-[#64748B] mb-3">
                    Réchauffeur HS, thermostat défaillant, carte électronique...
                  </p>
                  <span className="text-sm font-medium text-[#F97316] flex items-center gap-1">
                    Voir les réchauffeurs <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>

            {/* Panne 2: Spa disjoncte */}
            <Link 
              to="/pieces-detachees-spa?category=cartes-electroniques"
              className="bg-white rounded-xl p-6 border border-[#E2E8F0] hover:border-[#F97316] hover:shadow-lg transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-yellow-100 rounded-lg group-hover:bg-yellow-200 transition-colors">
                  <Zap className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A] mb-2 group-hover:text-[#F97316] transition-colors">
                    Mon spa disjoncte
                  </h3>
                  <p className="text-sm text-[#64748B] mb-3">
                    Carte électronique, réchauffeur en court-circuit, pompe...
                  </p>
                  <span className="text-sm font-medium text-[#F97316] flex items-center gap-1">
                    Voir les cartes électroniques <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>

            {/* Panne 3: Pompe bruyante */}
            <Link 
              to="/pieces-detachees-spa?category=pompes-massage"
              className="bg-white rounded-xl p-6 border border-[#E2E8F0] hover:border-[#F97316] hover:shadow-lg transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-orange-100 rounded-lg group-hover:bg-orange-200 transition-colors">
                  <Volume2 className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A] mb-2 group-hover:text-[#F97316] transition-colors">
                    Ma pompe spa est bruyante
                  </h3>
                  <p className="text-sm text-[#64748B] mb-3">
                    Roulement usé, pompe de massage ou circulation à remplacer...
                  </p>
                  <span className="text-sm font-medium text-[#F97316] flex items-center gap-1">
                    Voir les pompes <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>

            {/* Panne 4: Erreur FLO */}
            <Link 
              to="/pieces-detachees-spa?category=pompes-circulation"
              className="bg-white rounded-xl p-6 border border-[#E2E8F0] hover:border-[#F97316] hover:shadow-lg transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                  <AlertCircle className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A] mb-2 group-hover:text-[#F97316] transition-colors">
                    Erreur FLO sur mon spa
                  </h3>
                  <p className="text-sm text-[#64748B] mb-3">
                    Problème de circulation d'eau, pompe de circulation défaillante, filtre encrassé...
                  </p>
                  <span className="text-sm font-medium text-[#F97316] flex items-center gap-1">
                    Voir les pompes circulation <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>

            {/* Panne 5: Erreur OH */}
            <Link 
              to="/pieces-detachees-spa?category=rechauffeurs"
              className="bg-white rounded-xl p-6 border border-[#E2E8F0] hover:border-[#F97316] hover:shadow-lg transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-red-100 rounded-lg group-hover:bg-red-200 transition-colors">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A] mb-2 group-hover:text-[#F97316] transition-colors">
                    Erreur OH sur mon spa
                  </h3>
                  <p className="text-sm text-[#64748B] mb-3">
                    Surchauffe détectée, sonde température, réchauffeur, circulation...
                  </p>
                  <span className="text-sm font-medium text-[#F97316] flex items-center gap-1">
                    Voir les réchauffeurs <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>

            {/* Panne 6: Clavier ne répond plus */}
            <Link 
              to="/pieces-detachees-spa?category=claviers"
              className="bg-white rounded-xl p-6 border border-[#E2E8F0] hover:border-[#F97316] hover:shadow-lg transition-all group"
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                  <HelpCircle className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A] mb-2 group-hover:text-[#F97316] transition-colors">
                    Clavier spa ne répond plus
                  </h3>
                  <p className="text-sm text-[#64748B] mb-3">
                    Clavier HS, connexion défectueuse, remplacement Balboa, Gecko...
                  </p>
                  <span className="text-sm font-medium text-[#F97316] flex items-center gap-1">
                    Voir les claviers <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </div>
            </Link>
          </div>

          <div className="text-center mt-8">
            <p className="text-[#64748B] text-sm mb-4">
              Vous ne trouvez pas votre panne ? Notre équipe technique peut vous aider.
            </p>
            <a href="mailto:contact@atlantiquespaboutique.com">
              <Button variant="outline" className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white">
                <Mail className="w-4 h-4 mr-2" />
                Demander un diagnostic
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* ============================================= */}
      {/* SEO BOOST PARAGRAPH */}
      {/* ============================================= */}
      <section className="py-12 bg-white border-b border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#475569] text-base leading-relaxed">
            <strong className="text-[#0F172A]">Catalogue national</strong> de pièces détachées (pompes, filtres, électronique), accessoires et produits d'entretien pour spa. Expédition partout en France. <strong className="text-[#1E3A5F]">Bien vérifier les dimensions et références avant commande.</strong>
          </p>
        </div>
      </section>
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-10 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Accès rapide aux catégories populaires
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              { name: 'Pompes circulation', link: '/pieces-detachees/pompes-circulation', icon: '🔄' },
              { name: 'Pompes massage', link: '/pieces-detachees/pompes-massage', icon: '💪' },
              { name: 'Blower', link: '/pieces-detachees/blower', icon: '💨' },
              { name: 'Filtres', link: '/pieces-detachees/filtres', icon: '🔍' },
              { name: 'Produits traitement', link: '/entretien-spa/produits-traitement', icon: '🧪' },
              { name: 'Remplacement pompe', link: '/remplacement-pompe-spa-nantes', icon: '🔧' },
            ].map((item) => (
              <Link
                key={item.name}
                to={item.link}
                className="flex flex-col items-center gap-3 p-5 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0] hover:border-[#1E3A5F] hover:shadow-lg transition-all text-center group"
              >
                <span className="text-3xl">{item.icon}</span>
                <span className="text-sm font-medium text-[#475569] group-hover:text-[#1E3A5F] transition-colors">
                  {item.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================= */}
      {/* SERVICE BLOCK - Intervention locale */}
      {/* ============================================= */}
      <section className="py-16 bg-[#1E3A5F]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Besoin d'aide pour choisir ?
              </h2>
              <p className="text-white/80 text-lg leading-relaxed mb-8">
                Notre expert spa vous conseille sur le choix de vos pièces. <strong className="text-white">Bien vérifier les dimensions et références</strong> avant commande. Toutes nos pièces sont livrées <strong className="text-white">partout en France métropolitaine</strong>.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="mailto:contact@atlantiquespaboutique.com">
                  <Button size="lg" className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-xl font-semibold">
                    <Mail className="w-5 h-5 mr-2" />
                    Nous contacter
                  </Button>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-[#F97316] rounded-full flex items-center justify-center flex-shrink-0">
                    <Truck className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">Livraison France entière</h3>
                    <p className="text-white/70 text-sm">Expédition sous 48-72h partout en France métropolitaine</p>
                  </div>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">Besoin d'aide ?</h3>
                    <p className="text-white/70 text-sm">Contactez-nous avant commande en cas de doute</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Contact */}
      <section className="max-w-6xl mx-auto px-4">
        <ContactCTA category="Pièces & Accessoires" />
      </section>

      {/* ============================================= */}
      {/* EXPERTISE TECHNIQUE - Section SEO */}
      {/* ============================================= */}
      <section className="py-16 bg-white border-t border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Notre expertise technique spa
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Plus qu'une boutique en ligne, <strong>Atlantique Spa Boutique</strong> est votre partenaire technique 
              pour l'entretien et la réparation de votre spa.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Expertise 1 */}
            <div className="bg-[#F8FAFC] rounded-xl p-6 border border-[#E2E8F0]">
              <div className="w-12 h-12 bg-[#1E3A5F] rounded-lg flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-[#0F172A] mb-2">Spécialiste spa</h3>
              <p className="text-sm text-[#64748B]">
                Expertise reconnue sur les systèmes Balboa, Gecko, LX Whirlpool et Waterway. 
                Conseil personnalisé pour chaque projet.
              </p>
            </div>

            {/* Expertise 2 */}
            <div className="bg-[#F8FAFC] rounded-xl p-6 border border-[#E2E8F0]">
              <div className="w-12 h-12 bg-[#10B981] rounded-lg flex items-center justify-center mb-4">
                <Headphones className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-[#0F172A] mb-2">Conseil technique</h3>
              <p className="text-sm text-[#64748B]">
                Besoin d'aide pour identifier une pièce ? Notre équipe vous guide 
                pour trouver la référence compatible avec votre spa.
              </p>
            </div>

            {/* Expertise 3 */}
            <div className="bg-[#F8FAFC] rounded-xl p-6 border border-[#E2E8F0]">
              <div className="w-12 h-12 bg-[#F97316] rounded-lg flex items-center justify-center mb-4">
                <Wrench className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-[#0F172A] mb-2">Installation & SAV</h3>
              <p className="text-sm text-[#64748B]">
                Service d'installation et maintenance spa disponible en Loire-Atlantique, 
                Vendée, Morbihan et Maine-et-Loire.
              </p>
            </div>

            {/* Expertise 4 */}
            <div className="bg-[#F8FAFC] rounded-xl p-6 border border-[#E2E8F0]">
              <div className="w-12 h-12 bg-[#6366F1] rounded-lg flex items-center justify-center mb-4">
                <Package className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-[#0F172A] mb-2">Livraison France entière</h3>
              <p className="text-sm text-[#64748B]">
                Expédition sous 48-72h partout en France métropolitaine. 
                Livraison offerte dès 80€ TTC sur les pièces et accessoires.
              </p>
            </div>
          </div>

          {/* Marques compatibles */}
          <div className="mt-12 text-center">
            <h3 className="text-lg font-semibold text-[#0F172A] mb-4">Pièces compatibles grandes marques</h3>
            <div className="flex flex-wrap justify-center gap-4">
              {['Balboa', 'Gecko', 'Waterway', 'LX Whirlpool', 'Wellis', 'Canadian Spa', 'Hydropool', 'Dimension One', 'Hotspring', 'Sundance'].map((brand) => (
                <span key={brand} className="bg-white text-[#475569] text-sm px-5 py-2.5 rounded-lg border border-[#E2E8F0] font-medium">
                  {brand}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================= */}
      {/* SEO TEXT BLOCK - Long form content (300-500 words) */}
      {/* ============================================= */}
      <section className="py-16 bg-white border-t border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-6 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Pièces détachées pour spa : pompes, réchauffeurs, cartes électroniques et filtres
          </h2>
          
          <div className="prose prose-slate max-w-none text-[#475569] leading-relaxed">
            <p className="mb-4">
              <strong className="text-[#0F172A]">Atlantique Spa Boutique</strong> est votre spécialiste des <strong>pièces détachées spa</strong> en France. 
              Que vous soyez un particulier souhaitant réparer votre spa ou un professionnel à la recherche de pièces de rechange fiables, 
              notre catalogue couvre l'ensemble des besoins en maintenance et réparation de spas et jacuzzis.
            </p>
            
            <h3 className="text-lg font-semibold text-[#0F172A] mt-6 mb-3">Les différentes pièces détachées spa disponibles</h3>
            <p className="mb-4">
              Notre sélection comprend toutes les <strong>pièces détachées spa</strong> essentielles au bon fonctionnement de votre équipement : 
              <strong> pompes spa</strong> (pompes de massage mono et bi-vitesse, pompes de circulation silencieuses), 
              <strong> réchauffeurs spa</strong> pour maintenir la température idéale de votre eau, 
              <strong> claviers spa</strong> et panneaux de commande tactiles ou à boutons, 
              <strong> cartes électroniques spa</strong> qui constituent le cerveau de votre installation, 
              <strong> filtres spa</strong> indispensables à la qualité de l'eau, 
              ainsi que les blowers pour un massage aérien relaxant.
            </p>
            
            <h3 className="text-lg font-semibold text-[#0F172A] mt-6 mb-3">Compatibilité avec les grandes marques de spa</h3>
            <p className="mb-4">
              Nos <strong>pièces détachées spa</strong> sont sélectionnées pour leur compatibilité avec les principales marques du marché. 
              Que votre spa soit équipé d'un système <strong>Balboa</strong>, <strong>Gecko</strong>, <strong>Waterway</strong> ou <strong>LX Whirlpool</strong>, 
              nous avons les pièces adaptées. Nous proposons également des pièces compatibles avec les spas <strong>Wellis</strong>, 
              <strong>Canadian Spa</strong>, <strong>Hydropool</strong>, <strong>Dimension One</strong>, <strong>Hotspring</strong> et <strong>Sundance</strong>. 
              Avant toute commande, vérifiez les références et dimensions de vos pièces d'origine pour garantir une compatibilité optimale.
            </p>
            
            <h3 className="text-lg font-semibold text-[#0F172A] mt-6 mb-3">L'importance d'utiliser des pièces compatibles</h3>
            <p className="mb-4">
              Utiliser des <strong>pièces détachées spa</strong> de qualité et compatibles avec votre équipement est essentiel pour plusieurs raisons. 
              Une <strong>pompe spa</strong> inadaptée peut entraîner une surconsommation électrique ou un débit insuffisant. 
              Un <strong>réchauffeur spa</strong> mal dimensionné ne chauffera pas efficacement votre eau. 
              Une <strong>carte électronique spa</strong> non compatible peut provoquer des dysfonctionnements ou endommager d'autres composants. 
              C'est pourquoi notre équipe technique est à votre disposition pour vous conseiller et vous aider à identifier la bonne pièce.
            </p>
            
            <h3 className="text-lg font-semibold text-[#0F172A] mt-6 mb-3">Les pièces les plus fréquemment remplacées</h3>
            <p className="mb-4">
              Parmi les <strong>pièces détachées spa</strong> les plus demandées, on retrouve les <strong>pompes de circulation</strong> 
              (souvent les premières à montrer des signes d'usure après plusieurs années), les <strong>réchauffeurs</strong> 
              (qui peuvent s'entartrer ou se détériorer avec le temps), les <strong>claviers Balboa</strong> et <strong>Gecko</strong> 
              (sensibles à l'humidité et aux UV), ainsi que les <strong>filtres spa</strong> qui doivent être changés régulièrement 
              pour maintenir une eau cristalline. Une maintenance préventive avec des pièces de qualité prolonge significativement 
              la durée de vie de votre spa.
            </p>
            
            <p className="mt-6 text-center">
              <strong className="text-[#1E3A5F]">Livraison France entière sous 48-72h</strong> – Offerte dès 80€ TTC. 
              Un doute sur la pièce à commander ? <Link to="/contact" className="text-[#F97316] hover:underline">Contactez notre équipe technique</Link>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PiecesHubPage;
